import type { SerializeFrom } from '~/types/serialize-from';
import { useCallback, useEffect } from 'react';
import { useFetcher } from 'react-router';
import { useLatest } from 'ahooks';
import {
    UPDATE_CONVERSATION_API,
    type action,
} from '~/routes/remix-api.chat.update-conversation';
import {
    UPDATE_CONVERSATION_THUMBNAIL_API,
    type action as updateThumbnailAction,
} from '~/routes/remix-api.chat.update-thumbnail';
import {
    LEAVE_CONVERSATION_API,
    type action as leaveAction,
} from '~/routes/remix-api.chat.leave-conversation';
import {
    ADD_PARTICIPANTS_API,
    type action as addParticipantsAction,
} from '~/routes/remix-api.chat.add-participants';
import {
    REMOVE_PARTICIPANT_API,
    type action as removeParticipantAction,
} from '~/routes/remix-api.chat.remove-participant';
import {
    INVITE_TO_GROUP_CHAT_API,
    type action as inviteToGroupChatAction,
} from '~/routes/remix-api.chat.invite-to-group-chat';
import {
    UPDATE_CONVERSATION_STATUS_API,
    type action as updateStatusAction,
} from '~/routes/remix-api.chat.update-conversation-status';
import {
    MUTE_CONVERSATION_API,
    type action as muteAction,
} from '~/routes/remix-api.chat.mute';
import { useConversation } from '../context';
import { useConversationCache } from './use-conversation-cache';

type UpdateConversationResponse = NonNullable<
    SerializeFrom<typeof action>
>['conversation'];

/**
 * Hook to update the settings of a (direct) conversation.
 */
export function useUpdateConversation({
    conversationID,
    professionalID,
    onUpdateSuccess,
    onUpdateThumbnailSuccess,
    onLeaveSuccess,
    onAddSuccess,
    onRemoveSuccess,
    onInviteToGroupChatSuccess,
    onInviteToGroupChatError,
    onArchiveSuccess,
    onMuteSuccess,
}: {
    conversationID: number;
    /**
     * The professional ID if in a company inbox context.
     */
    professionalID: number | null;
    /**
     * Callback when the conversation settings are updated.
     */
    onUpdateSuccess?: (conversation: UpdateConversationResponse) => void;
    /**
     * Callback when the conversation thumbnail is updated.
     */
    onUpdateThumbnailSuccess?: () => void;
    /**
     * Callback when the user leaves the conversation.
     */
    onLeaveSuccess?: () => void;
    /**
     * Callback when participants are added to the conversation.
     */
    onAddSuccess?: () => void;
    /**
     * Callback when a participant is removed from the conversation.
     */
    onRemoveSuccess?: () => void;
    /**
     * Callback when participants are invited to a group chat.
     */
    onInviteToGroupChatSuccess?: (groupChatID: number) => void;
    /**
     * Callback when inviting participants to a group chat fails.
     */
    onInviteToGroupChatError?: (message: string) => void;
    /**
     * Callback when the conversation is archived/unarchived.
     */
    onArchiveSuccess?: (isArchived: boolean) => void;
    /**
     * Callback when the conversation is muted/unmuted.
     */
    onMuteSuccess?: (isMuted: boolean) => void;
}) {
    const { isParticipant } = useConversation();
    const {
        updateCachedConversation,
        replaceCachedConversationMessages,
        invalidateCachedConversation,
    } = useConversationCache();
    const updateFetcher = useFetcher<typeof action>();
    const updateThumbnailFetcher = useFetcher<typeof updateThumbnailAction>();
    const leaveFetcher = useFetcher<typeof leaveAction>();
    const addFetcher = useFetcher<typeof addParticipantsAction>();
    const removeFetcher = useFetcher<typeof removeParticipantAction>();
    const inviteToGroupChatFetcher =
        useFetcher<typeof inviteToGroupChatAction>();
    const updateStatusFetcher = useFetcher<typeof updateStatusAction>();
    const muteFetcher = useFetcher<typeof muteAction>();
    const latestOnUpdateSuccess = useLatest(onUpdateSuccess);
    const latestOnUpdateThumbnailSuccess = useLatest(onUpdateThumbnailSuccess);
    const latestOnLeaveSuccess = useLatest(onLeaveSuccess);
    const latestOnAddSuccess = useLatest(onAddSuccess);
    const latestOnRemoveSuccess = useLatest(onRemoveSuccess);
    const latestOnInviteToGroupChatSuccess = useLatest(
        onInviteToGroupChatSuccess,
    );
    const latestOnInviteToGroupChatError = useLatest(onInviteToGroupChatError);
    const latestOnArchiveSuccess = useLatest(onArchiveSuccess);
    const latestOnMuteSuccess = useLatest(onMuteSuccess);

    const updateConversation = useCallback(
        ({
            name,
            buildHubID,
        }: {
            name?: string | null;
            buildHubID?: number | null;
        }) => {
            if (conversationID <= 0 || !isParticipant) return;
            updateFetcher.submit(
                {
                    conversationID,
                    professionalID,
                    name: name ?? null,
                    buildHubID: buildHubID ?? null,
                },
                {
                    action: UPDATE_CONVERSATION_API,
                    method: 'POST',
                    encType: 'application/json',
                },
            );
        },
        [conversationID, isParticipant, professionalID, updateFetcher],
    );

    const updateConversationThumbnail = useCallback(
        (thumbnail: File | null | undefined) => {
            if (conversationID <= 0 || !isParticipant) return;
            const formData = new FormData();
            formData.append('conversationID', String(conversationID));
            if (professionalID) {
                formData.append('professionalID', String(professionalID));
            }
            if (thumbnail) {
                formData.append('thumbnail', thumbnail);
            }
            updateThumbnailFetcher.submit(formData, {
                action: UPDATE_CONVERSATION_THUMBNAIL_API,
                method: 'POST',
                encType: 'multipart/form-data',
            });
        },
        [conversationID, isParticipant, professionalID, updateThumbnailFetcher],
    );

    const leaveConversation = useCallback(() => {
        if (conversationID <= 0 || !isParticipant) return;
        leaveFetcher.submit(
            { conversationID, professionalID },
            {
                action: LEAVE_CONVERSATION_API,
                method: 'POST',
                encType: 'application/json',
            },
        );
    }, [conversationID, isParticipant, professionalID, leaveFetcher]);

    const addParticipants = useCallback(
        (memberIDs: number[]) => {
            if (conversationID <= 0 || !isParticipant) return;
            addFetcher.submit(
                { conversationID, professionalID, memberIDs },
                {
                    action: ADD_PARTICIPANTS_API,
                    method: 'POST',
                    encType: 'application/json',
                },
            );
        },
        [conversationID, isParticipant, professionalID, addFetcher],
    );

    const removeParticipant = useCallback(
        (participantID: string | number) => {
            if (conversationID <= 0 || !isParticipant) return;
            removeFetcher.submit(
                { conversationID, professionalID, participantID },
                {
                    action: REMOVE_PARTICIPANT_API,
                    method: 'POST',
                    encType: 'application/json',
                },
            );
        },
        [conversationID, isParticipant, professionalID, removeFetcher],
    );

    const inviteToGroupChat = useCallback(
        (memberIDs: number[], migrateMessagesFrom?: number | null) => {
            if (conversationID <= 0 || !isParticipant) return;
            inviteToGroupChatFetcher.submit(
                {
                    sourceConversationID: conversationID,
                    memberIDs,
                    migrateMessagesFrom: migrateMessagesFrom ?? null,
                },
                {
                    action: INVITE_TO_GROUP_CHAT_API,
                    method: 'POST',
                    encType: 'application/json',
                },
            );
        },
        [conversationID, isParticipant, inviteToGroupChatFetcher],
    );

    const markImportant = useCallback(
        (isImportant: boolean) => {
            if (conversationID <= 0 || !isParticipant) return;
            updateStatusFetcher.submit(
                { conversationID, professionalID, isImportant },
                {
                    action: UPDATE_CONVERSATION_STATUS_API,
                    method: 'POST',
                    encType: 'application/json',
                },
            );
            updateCachedConversation(conversationID, professionalID, {
                IsImportant: isImportant,
            });
        },
        [
            conversationID,
            isParticipant,
            professionalID,
            updateStatusFetcher,
            updateCachedConversation,
        ],
    );

    const archiveConversation = useCallback(
        (isArchived: boolean) => {
            if (conversationID <= 0 || !isParticipant) return;
            updateStatusFetcher.submit(
                { conversationID, professionalID, isArchived },
                {
                    action: UPDATE_CONVERSATION_STATUS_API,
                    method: 'POST',
                    encType: 'application/json',
                },
            );
            updateCachedConversation(conversationID, professionalID, {
                IsArchived: isArchived,
            });
        },
        [
            conversationID,
            isParticipant,
            professionalID,
            updateStatusFetcher,
            updateCachedConversation,
        ],
    );

    const markDone = useCallback(
        (isDone: boolean) => {
            if (conversationID <= 0 || !isParticipant) return;
            updateStatusFetcher.submit(
                { conversationID, professionalID, isDone },
                {
                    action: UPDATE_CONVERSATION_STATUS_API,
                    method: 'POST',
                    encType: 'application/json',
                },
            );
            updateCachedConversation(conversationID, professionalID, {
                IsDone: isDone,
            });
        },
        [
            conversationID,
            isParticipant,
            professionalID,
            updateStatusFetcher,
            updateCachedConversation,
        ],
    );

    const muteConversation = useCallback(
        (isMuted: boolean) => {
            if (conversationID <= 0 || !isParticipant) return;
            muteFetcher.submit(
                { conversationID, professionalID, isMuted },
                {
                    action: MUTE_CONVERSATION_API,
                    method: 'POST',
                    encType: 'application/json',
                },
            );
            // Optimistic UI.
            updateCachedConversation(conversationID, professionalID, {
                IsMuted: isMuted,
            });
        },
        [
            conversationID,
            isParticipant,
            professionalID,
            muteFetcher,
            updateCachedConversation,
        ],
    );

    // Run callback and update cached conversation when update conversation mutation succeeds.
    useEffect(() => {
        if (updateFetcher.data && updateFetcher.state !== 'submitting') {
            const updatedConversation = updateFetcher.data.conversation;
            const systemMessages = updateFetcher.data.systemMessages;
            updateCachedConversation(
                Number(updatedConversation.ID),
                professionalID,
                {
                    Name: updatedConversation.Name ?? null,
                    BuildHub: updatedConversation.BuildHub ?? null,
                    Messages: systemMessages,
                },
            );
            latestOnUpdateSuccess.current?.(updatedConversation);
        }
    }, [
        professionalID,
        updateFetcher,
        latestOnUpdateSuccess,
        updateCachedConversation,
    ]);

    // Run callback and update cached conversation when update conversation thumbnail mutation succeeds.
    useEffect(() => {
        if (
            updateThumbnailFetcher.data &&
            updateThumbnailFetcher.state !== 'submitting'
        ) {
            const updatedConversation =
                updateThumbnailFetcher.data.conversation;
            const systemMessages = updateThumbnailFetcher.data.systemMessages;
            updateCachedConversation(
                Number(updatedConversation.ID),
                professionalID,
                {
                    Thumbnail: updatedConversation.Thumbnail ?? null,
                    Messages: systemMessages,
                },
            );
            latestOnUpdateThumbnailSuccess.current?.();
        }
    }, [
        professionalID,
        updateThumbnailFetcher,
        latestOnUpdateThumbnailSuccess,
        updateCachedConversation,
    ]);

    // Run callback and invalidate cached conversation when leave conversation mutation succeeds.
    useEffect(() => {
        if (leaveFetcher.data && leaveFetcher.state !== 'submitting') {
            const updatedConversation = leaveFetcher.data.conversation;
            // User should no longer see the conversation they left.
            invalidateCachedConversation(
                Number(updatedConversation.ID),
                professionalID,
            );
            latestOnLeaveSuccess.current?.();
        }
    }, [
        professionalID,
        leaveFetcher,
        latestOnLeaveSuccess,
        invalidateCachedConversation,
    ]);

    // Run callback and update cached conversation when add participants mutation succeeds.
    useEffect(() => {
        if (addFetcher.data && addFetcher.state !== 'submitting') {
            const updatedConversation = addFetcher.data.conversation;
            const systemMessages = addFetcher.data.systemMessages;
            updateCachedConversation(
                Number(updatedConversation.ID),
                professionalID,
                {
                    Participants: updatedConversation.Participants,
                    Messages: systemMessages,
                },
            );
            latestOnAddSuccess.current?.();
        }
    }, [
        professionalID,
        addFetcher,
        latestOnAddSuccess,
        updateCachedConversation,
    ]);

    // Run callback and update cached conversation when remove participant mutation succeeds.
    useEffect(() => {
        if (removeFetcher.data && removeFetcher.state !== 'submitting') {
            const updatedConversation = removeFetcher.data.conversation;
            const systemMessages = removeFetcher.data.systemMessages;
            updateCachedConversation(
                Number(updatedConversation.ID),
                professionalID,
                {
                    Participants: updatedConversation.Participants,
                    Messages: systemMessages,
                },
            );
            latestOnRemoveSuccess.current?.();
        }
    }, [
        professionalID,
        removeFetcher,
        latestOnRemoveSuccess,
        updateCachedConversation,
    ]);

    // Run callback when invite to group chat mutation succeeds.
    useEffect(() => {
        if (
            inviteToGroupChatFetcher.data &&
            inviteToGroupChatFetcher.state === 'loading'
        ) {
            const response = inviteToGroupChatFetcher.data;
            if (response.__typename === 'InviteToGroupChatPayload') {
                const groupChat = response.Conversation;
                const sourceConversation = response.SourceConversation;
                if (response.SystemMessages.length) {
                    // Replace the messages of the source and target conversations
                    // if messages were migrated.
                    replaceCachedConversationMessages(
                        conversationID,
                        professionalID,
                        sourceConversation.Messages.nodes,
                    );
                    replaceCachedConversationMessages(
                        Number(groupChat.ID),
                        professionalID,
                        groupChat.Messages.nodes,
                    );
                }
                latestOnInviteToGroupChatSuccess.current?.(
                    Number(groupChat.ID),
                );
            } else {
                latestOnInviteToGroupChatError.current?.(response.Message);
            }
        }
    }, [
        conversationID,
        professionalID,
        inviteToGroupChatFetcher,
        latestOnInviteToGroupChatSuccess,
        latestOnInviteToGroupChatError,
        replaceCachedConversationMessages,
    ]);

    // Run callback and update cached conversation when status update mutation succeeds.
    useEffect(() => {
        if (
            updateStatusFetcher.data &&
            updateStatusFetcher.state === 'loading'
        ) {
            const updatedConversation = updateStatusFetcher.data.conversation;
            updateCachedConversation(
                Number(updatedConversation.ID),
                professionalID,
                {
                    IsImportant: updatedConversation.IsImportant,
                    IsArchived: updatedConversation.IsArchived,
                    IsDone: updatedConversation.IsDone,
                },
            );
            latestOnArchiveSuccess.current?.(updatedConversation.IsArchived);
        }
    }, [
        professionalID,
        updateStatusFetcher,
        latestOnArchiveSuccess,
        updateCachedConversation,
    ]);

    // Run callback and update cached conversation when mute conversation mutation succeeds.
    useEffect(() => {
        if (muteFetcher.data && muteFetcher.state === 'loading') {
            const updatedConversation = muteFetcher.data.conversation;
            updateCachedConversation(
                Number(updatedConversation.ID),
                professionalID,
                {
                    IsMuted: updatedConversation.IsMuted,
                },
            );
            latestOnMuteSuccess.current?.(updatedConversation.IsMuted);
        }
    }, [
        professionalID,
        muteFetcher,
        latestOnMuteSuccess,
        updateCachedConversation,
    ]);

    return {
        /**
         * One of the update fetchers are submitting.
         */
        isSubmitting:
            updateFetcher.state === 'submitting' ||
            updateThumbnailFetcher.state === 'submitting' ||
            leaveFetcher.state === 'submitting' ||
            addFetcher.state === 'submitting' ||
            removeFetcher.state === 'submitting' ||
            inviteToGroupChatFetcher.state === 'submitting' ||
            updateStatusFetcher.state === 'submitting' ||
            muteFetcher.state === 'submitting',
        /**
         * Update the conversation settings. Can update the following fields:
         * - Name (title).
         * - BuildHubID. Set to `0` to unlink the project.
         */
        updateConversation,
        /**
         * Update the conversation thumbnail. Only works for direct conversations.
         */
        updateConversationThumbnail,
        /**
         * Leave the conversation. Only works for group chats with more than 2 participants.
         */
        leaveConversation,
        /**
         * Add participants to the conversation. Only works for group chats.
         */
        addParticipants,
        /**
         * Remove a participant from the conversation. Only works for group chats with more than 2 participants.
         */
        removeParticipant,
        /**
         * Invite participants from a 1-on-1 chat to a group chat.
         * Finds or creates the group chat and optionally migrates messages.
         */
        inviteToGroupChat,
        /**
         * Mark/unmark the conversation as important for the current user.
         */
        markImportant,
        /**
         * Archive/unarchive the conversation for the current user.
         */
        archiveConversation,
        /**
         * Mark/unmark the conversation as done for the current user.
         */
        markDone,
        /**
         * Mute/unmute the conversation for the current user.
         */
        muteConversation,
    };
}
